import React, { useEffect } from 'react';
import { FiX } from 'react-icons/fi';

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  action, // Add action prop for header buttons
  size = 'xl', // sm, md, lg, xl, full
  showCloseButton = true,
  closeOnOverlayClick = true,
  className = ''
}) => {
  // Handle escape key and browser back button
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    const handlePopState = (e) => {
      // If the modal is open and we're going back, close the modal
      if (isOpen && window.history.state?.modalOpen) {
        onClose();
      }
    };

    // Add event listeners
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('popstate', handlePopState);
    
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    
    // Store the current scroll position
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    // Push a new entry to the history stack
    window.history.pushState({ modalOpen: true, scrollY }, '');

    return () => {
      // Remove event listeners
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('popstate', handlePopState);
      
      // Restore body scroll and position
      document.body.style.overflow = 'unset';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      
      // Restore scroll position if we have it
      if (window.history.state?.scrollY) {
        window.scrollTo(0, window.history.state.scrollY);
      }
      
      // Only go back in history if the current state is our modal state
      if (window.history.state?.modalOpen) {
        window.history.back();
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-sm mx-4 sm:mx-0',
    md: 'max-w-md mx-4 sm:mx-0',
    lg: 'max-w-lg mx-4 sm:mx-0',
    xl: 'max-w-2xl mx-4 sm:mx-0',
    '2xl': 'max-w-4xl mx-4 sm:mx-0',
    full: 'max-w-[100vw] max-h-[100vh] md:max-w-[95vw] md:max-h-[95vh]' // Full screen on mobile, 95% on desktop
  };

  const handleOverlayClick = (e) => {
    // Disable overlay click on mobile for full-size modals
    const isMobile = window.innerWidth < 768;
    if (closeOnOverlayClick && e.target === e.currentTarget && !(isMobile && size === 'full')) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleOverlayClick}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-2 sm:p-4">
        <div
          className={`
            relative w-full ${sizeClasses[size]} 
            bg-white dark:bg-gray-800 
            rounded-lg sm:rounded-2xl shadow-xl 
            transform transition-all
            flex flex-col
            ${className}
          `}
          style={{
            maxHeight: size === 'full' ? '100vh' : '95vh',
            height: size === 'full' ? '100vh' : 'auto'
          }}
        >
          {/* Header */}
          {(title || showCloseButton) && (
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <div className="flex items-center gap-3 overflow-hidden mr-4">
                {title && (
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-50 truncate">
                    {title}
                  </h3>
                )}
                {action && (
                  <div className="flex-shrink-0">
                    {action}
                  </div>
                )}
              </div>
              {showCloseButton && (
                <button
                  onClick={onClose}
                  className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  aria-label="Close modal"
                >
                  <FiX className="w-5 h-5" />
                </button>
              )}
            </div>
          )}

          {/* Content */}
          <div className={`flex-1 overflow-y-auto ${className.includes('pdf-viewer-modal') ? 'p-0' : 'p-4 sm:p-6'}`}>
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Modal;